import { motion, AnimatePresence, useAnimation } from "framer-motion";
import { useState, useEffect, useRef } from "react";
import Header from "@/components/sections/Header";
import Footer from "@/components/sections/Footer";
import GetInTouch from "@/components/sections/GetInTouch";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";

const useMousePosition = () => {
  // Removed global mouse listener for performance
  return { x: window.innerWidth / 2, y: window.innerHeight / 2 };
};

const processSteps = [
  {
    title: "Idea & Design",
    desc: "Brainstorming, sketching, and digital modeling. Creative concepts are transformed into practical designs.",
    image: "/images/Idea and design.jpg",
    icon: "💡",
    color: "#2C3E50" // Dark Blue-Gray
  },
  {
    title: "Prototyping",
    desc: "Creating samples and testing functionality. We refine every detail before mass production.",
    image: "/images/Prototyping.jpg",
    icon: "🛠️",
    color: "#34495E" // Wet Asphalt
  },
  {
    title: "Material Selection",
    desc: "Choosing the best materials for durability and quality. Our experts ensure only the finest resources are used.",
    image: "/images/Material selection.jpg",
    icon: "🧱",
    color: "#2C3E50" // Dark Blue-Gray
  },
  {
    title: "Production",
    desc: "Manufacturing with precision and care. Advanced technology meets skilled craftsmanship.",
    image: "/images/Production.jpg",
    icon: "🏭",
    color: "#2C3E50" // Dark Blue-Gray
  },
  {
    title: "Quality Control",
    desc: "Rigorous inspection at every stage. We guarantee excellence in every product.",
    image: "/images/Quality control.jpg",
    icon: "🔍",
    color: "#2C3E50" // Dark Blue-Gray
  },
  {
    title: "Packing & Delivery",
    desc: "Safe packaging and on-time delivery to our clients. Your satisfaction is our priority.",
    image: "/images/Packing and delivery.jpg",
    icon: "📦",
    color: "#2C3E50" // Dark Blue-Gray
  },
];

const AnimatedGradientBackground = ({ color1, color2, opacity = 0.1 }: { color1: string; color2: string; opacity?: number }) => {
  const { x, y } = useMousePosition();
  const ref = useRef<HTMLDivElement>(null);
  
  return (
    <div 
      ref={ref}
      className="absolute inset-0 -z-10 overflow-hidden"
      style={{
        background: `radial-gradient(circle at ${x}px ${y}px, ${color1} 0%, ${color2} 100%)`,
        opacity,
        transition: 'background 0.1s ease-out'
      }}
    />
  );
};

const ProductionProcess = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const controls = useAnimation();
  const currentStep = processSteps[currentIndex];

  const nextStep = async () => {
    setDirection(1);
    await controls.start({
      x: -50,
      opacity: 0,
      transition: { duration: 0.3 }
    });
    setCurrentIndex((prev) => (prev === processSteps.length - 1 ? 0 : prev + 1));
    controls.start({
      x: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    });
  };

  const prevStep = async () => {
    setDirection(-1);
    await controls.start({
      x: 50,
      opacity: 0,
      transition: { duration: 0.3 }
    });
    setCurrentIndex((prev) => (prev === 0 ? processSteps.length - 1 : prev - 1));
    controls.start({
      x: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    });
  };

  // Auto-advance carousel with pause on hover
  useEffect(() => {
    if (isHovered) return;
    const timer = setInterval(() => {
      nextStep();
    }, 5000);
    return () => clearInterval(timer);
  }, [currentIndex, isHovered]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  const imageVariants = {
    hidden: { scale: 1.1, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }
    },
    hover: {
      scale: 1.03,
      transition: { duration: 0.4 }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 overflow-x-hidden">
      <AnimatedGradientBackground 
        color1="#2C3E50" 
        color2="#1A252F" 
        opacity={0.05} 
      />
      
      <Header />
      
      <main className="relative z-10 pt-24 md:pt-28 lg:pt-32 pb-12 md:pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <motion.div 
            className="text-center mb-8 sm:mb-12 md:mb-20 px-2 sm:px-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <motion.span 
              className="inline-block text-xs sm:text-sm font-nexa-bold text-gray-500 mb-2 sm:mb-3 tracking-wider uppercase"
            >
              OUR MANUFACTURING JOURNEY
            </motion.span>
            <motion.h1 
              className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-nexa-bold uppercase tracking-tight mb-3 md:mb-4 px-2"
            >
              Crafting Excellence
            </motion.h1>
            <motion.p 
              className="text-sm sm:text-base md:text-lg text-gray-600 font-nexa-book max-w-3xl mx-auto leading-relaxed px-2 sm:px-4"
            >
              Discover the meticulous process behind our premium products, from concept to delivery.
            </motion.p>
          </motion.div>
          
          {/* Process Steps Carousel */}
          <div className="relative">
            {/* Navigation Arrows - Hidden on mobile, visible on md and up */}
            <div className="hidden md:flex absolute -top-16 right-0 left-0 md:top-1/2 md:-translate-y-1/2 justify-between px-2 z-20">
              <button 
                onClick={prevStep}
                className="p-2 rounded-sm bg-white/90 backdrop-blur-sm shadow-md hover:bg-white transition-colors transform hover:scale-110 active:scale-95"
                aria-label="Previous step"
              >
                <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5 text-gray-700" />
              </button>
              <button 
                onClick={nextStep}
                className="p-2 rounded-sm bg-white/90 backdrop-blur-sm shadow-md hover:bg-white transition-colors transform hover:scale-110 active:scale-95 md:ml-auto"
                aria-label="Next step"
              >
                <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 text-gray-700" />
              </button>
            </div>
            
            {/* Process Step Content */}
            <div className="relative h-auto min-h-[500px] sm:h-[500px] md:h-[550px] lg:h-[600px] overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentIndex}
                  className="absolute inset-0"
                  initial={{ x: direction > 0 ? '100%' : '-100%', opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ 
                    x: direction > 0 ? '-100%' : '100%', 
                    opacity: 0,
                    transition: { duration: 0.3 }
                  }}
                  transition={{ 
                    type: 'spring', 
                    stiffness: 150, 
                    damping: 25,
                    mass: 1,
                    restDelta: 0.001
                  }}
                >
                  <div className="h-full bg-white rounded-sm md:rounded-sm shadow-lg overflow-hidden flex flex-col md:flex-row border border-[#1E2B2A]/5">
                    {/* Content Side */}
                    <div className="w-full md:w-1/2 p-6 sm:p-8 md:p-10 lg:p-12 flex flex-col justify-center relative z-10">
                      <motion.div 
                        className="absolute -left-10 -top-10 w-24 h-24 sm:w-32 sm:h-32 rounded-full opacity-5"
                        style={{ backgroundColor: currentStep.color }}
                        animate={{
                          scale: [1, 1.1, 1],
                          x: [0, 5, 0],
                        }}
                        transition={{
                          duration: 10,
                          repeat: Infinity,
                          repeatType: "reverse"
                        }}
                      />
                      
                      <motion.div 
                        className="text-4xl sm:text-5xl md:text-6xl mb-4 sm:mb-6 w-16 h-16 sm:w-20 sm:h-20 rounded-sm sm:rounded-sm flex items-center justify-center shadow-lg bg-white border border-gray-100"
                        initial={{ scale: 0.8, opacity: 0, rotate: -10 }}
                        animate={{ 
                          scale: 1, 
                          opacity: 1, 
                          rotate: 0,
                        }}
                        transition={{ 
                          type: "spring", 
                          stiffness: 300, 
                          damping: 15,
                          delay: 0.2
                        }}
                      >
                        {currentStep.icon}
                      </motion.div>
                      
                      <motion.h2 
                        className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-nexa-bold mb-3 md:mb-4"
                        style={{ color: currentStep.color }}
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.3, duration: 0.6 }}
                      >
                        {currentStep.title}
                      </motion.h2>
                      
                      <motion.p 
                        className="text-sm sm:text-base text-gray-600 font-nexa-book mb-6 md:mb-8 leading-relaxed"
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.4, duration: 0.6 }}
                      >
                        {currentStep.desc}
                      </motion.p>
                      
                      <motion.div
                        className="mt-auto"
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.5, duration: 0.6 }}
                      >
                        <div className="flex items-center space-x-3">
                          <span className="text-xs sm:text-sm font-nexa-medium text-gray-500 whitespace-nowrap">
                            {currentIndex + 1} / {processSteps.length}
                          </span>
                          <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <motion.div 
                              className="h-full rounded-full"
                              style={{ 
                                width: `${((currentIndex + 1) / processSteps.length) * 100}%`,
                                backgroundColor: currentStep.color 
                              }}
                              initial={{ width: 0 }}
                              animate={{ 
                                width: `${((currentIndex + 1) / processSteps.length) * 100}%`,
                                transition: { duration: 0.8, ease: "easeInOut" }
                              }}
                            />
                          </div>
                        </div>
                      </motion.div>
                    </div>
                    
                    {/* Image Side */}
                    <div className="w-full md:w-1/2 h-64 sm:h-80 md:h-full relative overflow-hidden">
                      <motion.div 
                        className="absolute inset-0 bg-gray-50"
                        style={{ opacity: 0.8 }}
                      />
                      <motion.img
                        src={currentStep.image}
                        alt={currentStep.title}
                        className="absolute inset-0 w-full h-full object-fill"
                        initial={{ opacity: 0, scale: 1.1 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8, ease: "easeInOut" }}
                        whileHover={{ scale: 1.05 }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent md:bg-gradient-to-r md:from-transparent md:via-black/5 md:to-black/10" />
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
            
            {/* Step Indicators */}
            <div className="flex justify-center mt-6 sm:mt-8 space-x-2">
              {processSteps.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`h-1.5 sm:h-2 rounded-sm transition-all ${index === currentIndex ? 'w-6 sm:w-8 bg-gray-800' : 'w-3 sm:w-4 bg-gray-200 hover:bg-gray-300'}`}
                  aria-label={`Go to step ${index + 1}`}
                />
              ))}
            </div>
            

          </div>
        </div>
      </main>
      
      <GetInTouch />
      <Footer />
    </div>
  );
};

export default ProductionProcess;
